import React, { useState, useEffect, useContext, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  IconButton,
  Box,
} from '@mui/material';
import PropTypes from 'prop-types';
import { Edit, Delete } from '@mui/icons-material';

import axiosInstance from '../../api';
import StyledCard from '../../components/card/StyledCard';
import { authContext } from '../../routes';
import SERVER_API_URL from '../../config';
import LocaleText from '../../utils/localText';
import Iconify from '../../components/iconify';
import { ethDate } from '../../utils/ethDate';

export default function DistrictList({
  setDistrictToBeUpdated,
  setDistrictActivatedOn,
  setChurchToBeUpdated,
  setUserToBeUpdated,
}) {
  const [districtList, setDistrictList] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState();
  const { user } = useContext(authContext);

  const columns = [
    { headerClassName: 'super-app-theme--header', field: 'name', width: 200, headerName: 'የወረዳ ስም' },
    { headerClassName: 'super-app-theme--header', field: 'description', width: 200, headerName: 'የወረዳ መግለጫ' },
    { headerClassName: 'super-app-theme--header', field: 'history', width: 100, headerName: 'ታሪክ' },
    { headerClassName: 'super-app-theme--header', field: 'phone', width: 150, headerName: 'ስልክ ቁጥር' },
    { headerClassName: 'super-app-theme--header', field: 'email', width: 200, headerName: 'ኢሜይል' },
    {
      headerClassName: 'super-app-theme--header', field: 'churchCount',
      width: 100,
      headerName: 'አጥቢያ',
    },
    { headerClassName: 'super-app-theme--header', field: 'usersCount', width: 150, headerName: 'ተከታዮች' },
    {
      headerClassName: 'super-app-theme--header', field: 'actions',
      width: 160,
      headerName: 'ድርጊቶች',
      renderCell: (params) => {
        const handleChurchInfo = async () => {
          try {
            const response = await axiosInstance.get(`${SERVER_API_URL}/church/findManyByDistrictId/${params.id}`);
            if (response.status === 200) {
              setChurchToBeUpdated(response.data);
              setDistrictActivatedOn('churchList');
            }
          } catch (error) {
            // console.log(error)
          }
        };

        const handleUserInfo = async () => {
          try {
            const response = await axiosInstance.post(`${SERVER_API_URL}/user/findManyByDistrictId/${params.id}`);
            if (response.status === 200) {
              setUserToBeUpdated(response.data);
              setDistrictActivatedOn('userList');
            }
          } catch (error) {
            // console.log(error)
          }
        };

        const handleEdit = async () => {
          try {
            const response = await axiosInstance.get(`${SERVER_API_URL}/district/findOneById/${params.id}`);
            if (response.status === 200) {
              setDistrictToBeUpdated(response.data);
              setDistrictActivatedOn('update');
            }
          } catch (error) {
            // console.log(error)
          }
        };

        const handleDelete = async () => {
          try {
            const response = await axiosInstance.get(`${SERVER_API_URL}/district/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedDistrict(response.data);
              setOpenDelete(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };

        return (
          <>
            <Tooltip title="አጥቢያዎች" color="primary" arrow>
              <IconButton disabled={!(user && user.role.permissions.church.read)} onClick={handleChurchInfo}>
                <Iconify icon="noto:church" />
              </IconButton>
            </Tooltip>
            <Tooltip title="ተከታዮች" color="success" arrow>
              <IconButton disabled={!(user && user.role.permissions.user.read)} onClick={handleUserInfo}>
                <Iconify icon="fluent:people-team-16-filled" />
              </IconButton>
            </Tooltip>
            <Tooltip title="አርትዕ" color="warning" arrow>
              <IconButton disabled={!(user && user.role.permissions.district.update)} onClick={handleEdit}>
                <Edit sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="ሰርዝ" color="error" arrow>
              <IconButton disabled={!(user && user.role.permissions.district.delete)} onClick={handleDelete}>
                <Delete sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      headerClassName: 'super-app-theme--header', field: 'createdAt',
      headerName: 'የተመዘገበበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
    {
      headerClassName: 'super-app-theme--header', field: 'updatedAt',
      headerName: 'የተስተካከለበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
  ];

  const handleOk = async () => {
    try {
      const response = await axiosInstance.delete(
        `${SERVER_API_URL}/district/deleteStatusOneById/${selectedDistrict.id}`
      );
      if (response.status === 200) {
        getAllDistrict();
        setOpenDelete(false);
      }
    } catch (error) {
      // console.log(error)
    }
  };

  const getAllDistrict = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`${SERVER_API_URL}/district/findAll`);

      if (response.status === 200) {
        console.log(response.data);
        setDistrictList(response.data);
      }
    } catch (error) {
      // console.log(error);
    }
  }, []);

  useEffect(() => {
    getAllDistrict();
  }, [getAllDistrict]);

  return (
    <>
      <StyledCard sx={{
        '& .super-app-theme--header': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.darkMode.lighter : theme.palette.grey[400],
        }
      }}>
        <DataGrid
          sx={{ p: 2, pt: 0, height: '75vh', '& .MuiDataGrid-row': { borderTopColor: (theme) => theme.palette.secondary.darker, borderWidth: 0.5, borderTopStyle: 'dashed' } }}
          rows={districtList}
          columns={columns}
          pageSize={12}
          initialState={{
            filter: {
              filterModel: {
                items: [],
                quickFilterValues: [''],
              },
            },
            pagination: { pageSize: 10 },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableSelectionOnClickCell
          disableColumnFilter
          localeText={LocaleText.pagination}
        />
      </StyledCard>
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
      <Box sx={{ backgroundColor: (theme) => theme.palette.mode === 'light' ? '#F9F9FB' : '#272424' }}>
        <DialogTitle>እርግጠኛ ንዎት {selectedDistrict && selectedDistrict.name} ወረዳን መሰረዝ ይፈልጋሉ?</DialogTitle>
        <DialogContent>
          <DialogContentText>በዚህ ወረዳ ስር ያሉ ሁሉም አብያተ-ክርስቲያኖች እና ተከታዮች ይጠፋሉ!</DialogContentText>
          <DialogContentText>ይህ እርምጃ ሊቀለበስ አይችልም።</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>ተመለስ</Button>
          <Button color="error" variant="contained" onClick={handleOk} autoFocus>
            ሰርዝ
          </Button>
        </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}

// Add this declaration near the top of your file
DistrictList.propTypes = {
  setDistrictToBeUpdated: PropTypes.func.isRequired,
  setDistrictActivatedOn: PropTypes.func.isRequired,
  setChurchToBeUpdated: PropTypes.func.isRequired,
  setUserToBeUpdated: PropTypes.func.isRequired,
};
